import type { TextFieldProps } from '@mui/material/TextField';

import { splitMath } from '@classroom-ai/types';
import { useMemo, useState, forwardRef } from 'react';

import TextField from '@mui/material/TextField';

import { MathText } from './math-text';

// ----------------------------------------------------------------------

type PreviewProps = {
  value?: unknown;
  className?: string;
  onActivate?: () => void;
};

// Stands in for the <input>/<textarea> while the field is idle, so the outline, label
// and sizing stay MUI's own. Focusing or clicking it hands control back to the editor.
const MathPreviewInput = forwardRef<HTMLDivElement, PreviewProps>(function MathPreviewInput(
  { value, className, onActivate },
  ref
) {
  return (
    <div
      ref={ref}
      role="textbox"
      tabIndex={0}
      className={className}
      style={{ height: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', cursor: 'text' }}
      onFocus={onActivate}
      onClick={onActivate}
    >
      <MathText text={String(value ?? '')} />
    </div>
  );
});

/**
 * A TextField for strings that carry `$…$` LaTeX. While idle it shows the math rendered
 * with KaTeX; on focus it swaps to the ordinary text input so the raw LaTeX can be
 * edited. Values without math (and `select` fields) behave exactly like TextField.
 */
export function MathTextField(props: TextFieldProps) {
  const { value, select, slotProps, onBlur, autoFocus } = props;
  const [editing, setEditing] = useState(false);
  const hasMath = useMemo(
    () => typeof value === 'string' && splitMath(value).some((s) => s.kind === 'math'),
    [value]
  );

  if (editing || !hasMath || select) {
    return (
      <TextField
        {...props}
        autoFocus={editing || autoFocus}
        onBlur={(e) => {
          setEditing(false);
          onBlur?.(e);
        }}
      />
    );
  }

  const input = (slotProps?.input ?? {}) as object;
  const htmlInput = (slotProps?.htmlInput ?? {}) as object;
  return (
    <TextField
      {...props}
      // The preview is a <div>, so drop the textarea-only behavior.
      multiline={false}
      minRows={undefined}
      maxRows={undefined}
      rows={undefined}
      slotProps={{
        ...slotProps,
        input: { ...input, inputComponent: MathPreviewInput as never },
        htmlInput: { ...htmlInput, onActivate: () => setEditing(true) },
        inputLabel: { ...((slotProps?.inputLabel ?? {}) as object), shrink: true },
      }}
    />
  );
}
